import { Component, inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  selectUser,
  selectUsers,
  selectUserStats,
} from '../../../state/admin.selector';
import {
  loadUserById,
  loadUsers,
  loadUserStatsByPeriod,
} from '../../../state/admin.actions';
import { DateUtils } from '../../../../../common/helpers/date-utils';
import { filter, map } from 'rxjs/operators';
import { ChartCardComponent } from '../../components/chart-card/chart-card.component';
import { Observable, take } from 'rxjs';
import {
  ChartData,
  getChartSeriesFromFiles,
} from '../../../../../common/helpers/chart-utils';
import { ChartFileInfo } from '../../../domain/models/file/chart-file-info';
import { UserStats } from '../../../domain/models/user/user-stats';
import { UserStatusModalComponent } from '../../components/user-status-modal/user-status-modal.component';
import { UserService } from '../../../data/services/user.service';
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatepickerUtils } from '../../../../../common/helpers/date-picker-utils';

@Component({
  imports: [
    CommonModule,
    ChartCardComponent,
    UserStatusModalComponent,
    RouterLink,
    NgxDaterangepickerBootstrapDirective,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  selector: 'app-user-details',
  standalone: true,
  styleUrl: './user-details.component.scss',
  templateUrl: './user-details.component.html',
})
export class UserDetailsComponent implements OnInit {
  userService = inject(UserService);
  translate = inject(TranslateService);
  datePickerUtils = inject(DatepickerUtils);
  selectedDateRange: { startDate: Date; endDate: Date } = {
    startDate: new Date(
      new Date().getFullYear() - 1,
      new Date().getMonth(),
      new Date().getDate(),
    ),
    endDate: new Date(),
  };
  showModal: boolean = false;
  protected datepickerLocale = {};
  protected readonly dateUtils = inject(DateUtils);
  protected chartFiles$: Observable<ChartFileInfo[]> = new Observable<
    ChartFileInfo[]
  >();
  protected chartData$: Observable<ChartData> = new Observable<ChartData>();
  private store = inject(Store);
  protected user$ = this.store.select(selectUser);
  protected userStats$ = this.store.select(selectUserStats);
  private route = inject(ActivatedRoute);

  ngOnInit(): void {
    this.updateLocale();

    this.translate.onLangChange.subscribe(() => {
      this.updateLocale();
    });

    const userId = this.route.snapshot.paramMap.get('id');
    if (userId) {
      this.store.dispatch(loadUserById({ id: userId }));

      this.user$
        .pipe(
          map((user) => user?.oidcId),
          filter((id): id is string => !!id),
          take(1),
        )
        .subscribe((oidcId) => {
          this.store.dispatch(
            loadUserStatsByPeriod({
              oidcId,
              startDate: this.selectedDateRange.startDate,
              endDate: this.selectedDateRange.endDate,
            }),
          );
        });

      this.chartFiles$ = this.userStats$.pipe(
        map((stats: UserStats | null) => stats?.chartFiles ?? []),
      );

      this.chartData$ = getChartSeriesFromFiles(
        this.chartFiles$,
        this.selectedDateRange.startDate,
        this.selectedDateRange.endDate,
      );
    }
  }

  updateLocale() {
    this.datePickerUtils.getDatepickerLocale().subscribe((locale) => {
      this.datepickerLocale = locale;
    });
  }

  getRecentEntryHtml(file: any): string {
    const fileTypeColorClass =
      file.fileType === 'Gerber'
        ? 'bg-[#E6F1FA] text-[#0069BC] px-2 py-[0.15rem] rounded-full text-sm'
        : file.fileType === 'Lectra'
          ? 'bg-[#FEF0F2] text-[#853844] px-2 py-[0.15rem] rounded-full text-sm'
          : '';

    const fileTypeSpan = `<span class="${fileTypeColorClass}">${file.fileType}</span>`;

    return this.translate.instant('admin.userDetails.recentEntryHtml', {
      type: fileTypeSpan,
      file: file.originalFileName,
      date: this.dateUtils.formatIsoToDateTime(file.createdAt),
    });
  }

  onDateRangeChange(dateRange: any) {
    this.selectedDateRange = dateRange;
    this.user$
      .pipe(
        map((user) => user?.oidcId),
        filter((id): id is string => !!id),
        take(1),
      )
      .subscribe((oidcId) => {
        this.store.dispatch(
          loadUserStatsByPeriod({
            oidcId,
            startDate: this.selectedDateRange.startDate,
            endDate: this.selectedDateRange.endDate,
          }),
        );
      });
  }

  openStatusModal(): void {
    this.showModal = true;
  }

  closeStatusModal(): void {
    this.showModal = false;
  }

  confirmUpdateStatus(): void {
    this.user$.pipe(take(1)).subscribe((user) => {
      if (!user) return;

      this.userService.updateUserStatus(user.id).subscribe(() => {
        this.store
          .select(selectUsers)
          .pipe(take(1))
          .subscribe((u) => {
            this.store.dispatch(
              loadUsers({
                page: u.page,
                pageSize: u.pageSize,
                search: u.search ?? '',
              }),
            );
          });

        this.closeStatusModal();
      });
    });
  }
}
